import { Component, computed, input } from '@angular/core';

interface InlinePart {
  text: string;
  emphasis: 'strong' | 'em' | null;
}

type SummaryBlock =
  | { type: 'heading'; parts: InlinePart[] }
  | { type: 'paragraph'; parts: InlinePart[] }
  | { type: 'list'; items: InlinePart[][] };

@Component({
  selector: 'app-console-summary-markdown',
  template: `
    <div class="summary-markdown">
      @for (block of blocks(); track $index) {
        @switch (block.type) {
          @case ('heading') {
            <h3>@for (part of block.parts; track $index) {@switch (part.emphasis) {@case ('strong') {<strong>{{ part.text }}</strong>} @case ('em') {<em>{{ part.text }}</em>} @default {{{ part.text }}}}}</h3>
          }
          @case ('paragraph') {
            <p>@for (part of block.parts; track $index) {@switch (part.emphasis) {@case ('strong') {<strong>{{ part.text }}</strong>} @case ('em') {<em>{{ part.text }}</em>} @default {{{ part.text }}}}}</p>
          }
          @case ('list') {
            <ul>
              @for (item of block.items; track $index) {
                <li>@for (part of item; track $index) {@switch (part.emphasis) {@case ('strong') {<strong>{{ part.text }}</strong>} @case ('em') {<em>{{ part.text }}</em>} @default {{{ part.text }}}}}</li>
              }
            </ul>
          }
        }
      }
    </div>
  `,
  styles: `
    :host { display: block; }
    .summary-markdown > :first-child { margin-top: 0; }
    .summary-markdown > :last-child { margin-bottom: 0; }
    h3 { margin: 0.9rem 0 0.35rem; font: 700 14px/1.5 var(--font-body, inherit); }
    p, li { font: 400 13px/1.6 var(--font-body, inherit); }
    p { margin: 0 0 0.55rem; }
    ul { margin: 0 0 0.65rem; padding-left: 1.25rem; }
  `,
})
export class ConsoleSummaryMarkdownComponent {
  text = input.required<string>();
  blocks = computed(() => parseSummary(this.text()));
}

function parseSummary(text: string): SummaryBlock[] {
  const blocks: SummaryBlock[] = [];
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  let paragraph: string[] = [];
  let list: InlinePart[][] = [];

  const flushParagraph = (): void => {
    if (paragraph.length) {
      blocks.push({ type: 'paragraph', parts: parseInline(paragraph.join(' ')) });
      paragraph = [];
    }
  };
  const flushList = (): void => {
    if (list.length) {
      blocks.push({ type: 'list', items: list });
      list = [];
    }
  };

  for (const line of lines) {
    const heading = line.match(/^###\s+(.+?)\s*#*\s*$/);
    const bullet = line.match(/^\s*[-*]\s+(.+?)\s*$/);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'heading', parts: parseInline(heading[1]) });
    } else if (bullet) {
      flushParagraph();
      list.push(parseInline(bullet[1]));
    } else if (!line.trim()) {
      flushParagraph();
      flushList();
    } else {
      flushList();
      paragraph.push(line.trim());
    }
  }
  flushParagraph();
  flushList();
  return blocks;
}

function parseInline(text: string): InlinePart[] {
  const parts: InlinePart[] = [];
  const pattern = /\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let lastIndex = 0;
  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > lastIndex) parts.push({ text: text.slice(lastIndex, index), emphasis: null });
    const strong = match[1] !== undefined;
    parts.push({ text: match[1] ?? match[2], emphasis: strong ? 'strong' : 'em' });
    lastIndex = index + match[0].length;
  }
  if (lastIndex < text.length) parts.push({ text: text.slice(lastIndex), emphasis: null });
  return parts;
}
